import { useEffect, useState } from 'react'
import { ServiceType, useGetVendorsInRangeLocationsLazyQuery } from '@/generated/graphql'
import { basketLocationToAddress } from '@caterdesk/utils--gm-validation'
import { getCountryCodeFromName } from '@/helpers/multiRegion/countries'
import { getDateTimeObject } from '../../domain/user-basket-settings'
import sortBy from 'lodash/sortBy'
import { useAppState } from './app'

type Props = {
  vendorId: string
}

export const useVendorInRange = ({ vendorId }: Props) => {
  const { userState } = useAppState()

  const [deliveryFee, setDeliveryFee] = useState<number>(0)
  const [minOrderValueExTax, setMinOrderValueExTax] = useState<number | null>(null)

  const [getVendorsInRangeLocations] = useGetVendorsInRangeLocationsLazyQuery({
    fetchPolicy: 'network-only',
    onError: (error) => {
      console.error('Error fetching vendors in range locations:', error)
    },
  })

  useEffect(() => {
    const location = userState?.location
    if (!location) return

    const { latitude, longitude, country } = basketLocationToAddress(location)
    if (!latitude || !longitude) return

    const countryCode = getCountryCodeFromName(country) || country || null
    const dateTimeObject = getDateTimeObject({ date: userState.date, time: userState.time })
    const dateTime = dateTimeObject ? { ...dateTimeObject, timezone: 'UTC' } : null

    getVendorsInRangeLocations({
      variables: {
        vendorId,
        lat: latitude,
        lng: longitude,
        serviceType: ServiceType.Gm,
        countryCode,
        dateTime,
      },
      onCompleted: (data) => {
        const closestLocation = data.vendor.inRangeLocations[0]
        if (!closestLocation) return

        const sortedRegions = sortBy(closestLocation.deliveryRegions, (r) => r.orderPosition)
        const gmRegion = sortedRegions.find((r) => r.serviceType === ServiceType.Gm)

        const fee = gmRegion?.deliveryFee?.total
        const minOrder =
          gmRegion?.minOrderValueExTax ??
          (gmRegion?.minOrderValueExTax ? gmRegion.minOrderValueExTax * 100 : null)
        if (fee !== undefined) setDeliveryFee(fee)
        else console.error('Delivery fee not found')

        if (minOrder !== null) setMinOrderValueExTax(minOrder)
        else console.error(`Minimum order value for region ${gmRegion?.id} not found`)
      },
    })
  }, [vendorId, getVendorsInRangeLocations, userState?.location, userState?.date, userState?.time])

  return {
    deliveryFee,
    minOrderFee: minOrderValueExTax,
  }
}
